import type { MapPaddingObject } from "@ui/components/Map";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { useHasMounted } from "@ui/hooks/use-has-mounted";
import { useEffect, useMemo, useState } from "react";
import { mobileFitPadding, mobileSelectionPadding } from "@/lib/mobile-search-map";

export interface MobileMapConfig {
  /** True below `lg`, after mount (SSR ships the desktop layout; mobile swaps in post-hydration). */
  useMobileLayout: boolean;
  /** fitBounds padding reserving the sheet-occluded lower map band; `undefined` on desktop. */
  fitPadding: MapPaddingObject | undefined;
  /** easeTo padding lifting a selected pin above the peek sheet + floating card; `undefined` on desktop. */
  selectionPadding: MapPaddingObject | undefined;
}

/**
 * Shared mobile-map plumbing for the programs/destinations search adapters: the client-mount +
 * breakpoint gate plus the map padding, tracked against the live viewport height so a rotation
 * re-frames correctly (the fit padding is derived from that height rather than frozen at mount).
 */
export function useMobileMapConfig(): MobileMapConfig {
  const hasMounted = useHasMounted();
  const isDesktop = useBreakpoint("lg");
  const hasDock = !useBreakpoint("md");
  const useMobileLayout = hasMounted && !isDesktop;

  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === "undefined" ? 0 : window.innerHeight));
  useEffect(() => {
    if (!useMobileLayout) return;
    const update = () => setViewportHeight(window.innerHeight);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [useMobileLayout]);

  return useMemo(
    () => ({
      useMobileLayout,
      fitPadding: useMobileLayout ? mobileFitPadding(viewportHeight) : undefined,
      selectionPadding: useMobileLayout ? mobileSelectionPadding({ hasDock, viewportHeight }) : undefined,
    }),
    [hasDock, useMobileLayout, viewportHeight],
  );
}
